import React, { useEffect, useState } from 'react';
import AdminShell from './AdminShell';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Package, Copy, Eye, EyeOff, ShieldCheck, Terminal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminExportPage() {
  const { user, loading: authLoading } = useAuth();
  const [info, setInfo] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem('cv_token');
    fetch(`${API}/api/admin/demos/export-secret`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setInfo)
      .catch(() => toast.error('Impossible de charger le secret (admin requis)'));
  }, []);

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copié`);
  };

  if (authLoading) return <AdminShell><div className="p-12 text-white/50">Chargement…</div></AdminShell>;
  if (!user?.is_admin) return <AdminShell><div className="p-12 text-center text-white/60">Accès réservé aux administrateurs.</div></AdminShell>;

  const url = info ? `${API}${info.endpoint}` : '';
  const curlCmd = info ? `curl -L -o pack-demos.zip "${url}" -H "${info.header}: ${info.secret}"` : '';

  return (
    <AdminShell>
      <div className="max-w-3xl space-y-6" data-testid="admin-export-page">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-400 mb-1">
            <Package className="w-3.5 h-3.5" /> Export inter-sites
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Pack 8 démos pédagogiques</h1>
          <p className="text-sm text-white/55 mt-1">
            Export sécurisé de la section « Comprends en 30 secondes » vers code2roues.fr.
            Protocole identique aux packs Annuaire / Livret AM : endpoint protégé par header secret.
          </p>
        </div>

        <div className="rounded-2xl bg-[#141416] border border-white/10 p-5 space-y-4">
          <div>
            <div className="text-white/50 text-[11px] uppercase tracking-wider font-semibold mb-1.5">URL de téléchargement</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[12.5px] text-emerald-300 bg-black/40 rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap" data-testid="export-url">{url || '…'}</code>
              <button onClick={() => copy(url, 'URL')} className="shrink-0 w-9 h-9 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" data-testid="copy-export-url"><Copy size={14} /></button>
            </div>
          </div>

          <div>
            <div className="text-white/50 text-[11px] uppercase tracking-wider font-semibold mb-1.5">Secret (header X-Export-Secret)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[12.5px] text-amber-300 bg-black/40 rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap" data-testid="export-secret">
                {info ? (show ? info.secret : '•'.repeat(24)) : '…'}
              </code>
              <button onClick={() => setShow(!show)} className="shrink-0 w-9 h-9 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" data-testid="toggle-export-secret">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              <button onClick={() => info && copy(info.secret, 'Secret')} className="shrink-0 w-9 h-9 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" data-testid="copy-export-secret"><Copy size={14} /></button>
            </div>
          </div>

          <div>
            <div className="text-white/50 text-[11px] uppercase tracking-wider font-semibold mb-1.5 inline-flex items-center gap-1.5"><Terminal size={12} /> Commande à transmettre au site frère</div>
            <div className="flex items-start gap-2">
              <code className="flex-1 text-[11.5px] text-white/80 bg-black/40 rounded-lg px-3 py-2 overflow-x-auto whitespace-pre" data-testid="export-curl">{info ? (show ? curlCmd : curlCmd.replace(info.secret, '•'.repeat(12))) : '…'}</code>
              <button onClick={() => info && copy(curlCmd, 'Commande curl')} className="shrink-0 w-9 h-9 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" data-testid="copy-export-curl"><Copy size={14} /></button>
            </div>
          </div>
        </div>

        {info && (
          <div className="rounded-2xl bg-[#141416] border border-white/10 p-5">
            <div className="text-white/50 text-[11px] uppercase tracking-wider font-semibold mb-2">Contenu du ZIP</div>
            <ul className="text-[12.5px] text-white/70 space-y-1 font-mono">
              {info.files.map((f) => <li key={f}>pack-demos/{f}</li>)}
            </ul>
          </div>
        )}

        <p className="text-[11.5px] text-white/40 flex items-start gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
          Le secret vit dans backend/.env (EXPORT_SECRET). Aucun bouton public n'expose ce pack :
          seul ce back-office et le header secret permettent le téléchargement. Transmets le secret
          en privé uniquement.
        </p>
      </div>
    </AdminShell>
  );
}
